export const CUSTOM_LOCK_MESSAGE = 'Simpan atau batalkan pengaturan Custom dulu';

export interface SlotRef {
  publicId: string;
  start: string;
  end: string;
}

const WEEKDAY_ID = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
const WEEKDAY_SHORT = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const MONTH_ID = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

export function paymentCardClass(status: string): string {
  if (status === 'paid') return 'border-emerald-200 bg-emerald-100 text-emerald-900';
  if (status === 'dp_paid') return 'border-amber-200 bg-amber-50 text-amber-900';
  return 'border-rose-200 bg-rose-100 text-rose-900';
}

export function isSlotOngoing(date: string, start: string, end: string, now: Date): boolean {
  if (date !== toDateKey(now)) return false;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);
  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}

export function isSlotPast(date: string, end: string, now: Date): boolean {
  const todayKey = toDateKey(now);
  if (date < todayKey) return true;
  if (date > todayKey) return false;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const endMinutes = toMinutes(end);
  return currentMinutes >= endMinutes;
}

export function isSlotHourPassed(date: string, end: string, now: Date = new Date()): boolean {
  return isSlotPast(date, end, now);
}

export function formatTimeChip(start: string, end: string): string {
  const s = start.endsWith(':00') ? start.slice(0, 2) : start.slice(0, 5);
  const e = end.endsWith(':00') ? end.slice(0, 2) : end.slice(0, 5);
  return `${s}-${e}`;
}

function formatDurationMinutes(minutes: number): string {
  if (minutes <= 0) return '0 jam';
  if (minutes % 60 === 0) return `${minutes / 60} jam`;
  if (minutes > 60 && minutes % 30 === 0) return `${String(minutes / 60).replace('.', ',')} jam`;
  return `${minutes} menit`;
}

export function durationLabel(start: string, end: string): string {
  return formatDurationMinutes(toMinutes(end) - toMinutes(start));
}

export function resolveSlotPublicIds(slots: SlotRef[], start: string, end: string): string[] | null {
  const ordered = [...slots].sort((a, b) => a.start.localeCompare(b.start));
  const picked = ordered.filter((slot) => slot.start >= start && slot.end <= end);
  if (picked.length === 0 || picked[0].start !== start || picked[picked.length - 1].end !== end) {
    return null;
  }
  for (let index = 1; index < picked.length; index += 1) {
    if (picked[index].start !== picked[index - 1].end) return null;
    const previous = ordered.findIndex((slot) => slot.publicId === picked[index - 1].publicId);
    const current = ordered.findIndex((slot) => slot.publicId === picked[index].publicId);
    if (current !== previous + 1) return null;
  }
  return picked.map((slot) => slot.publicId);
}

export function formatSelectedSlotRanges(slots: { start: string; end: string }[]): string {
  if (slots.length === 0) return '-';
  const sorted = [...slots].sort((a, b) => a.start.localeCompare(b.start));
  const ranges: { start: string; end: string }[] = [];

  let current = { start: sorted[0].start, end: sorted[0].end };
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index].start === current.end) {
      current.end = sorted[index].end;
    } else {
      ranges.push(current);
      current = { start: sorted[index].start, end: sorted[index].end };
    }
  }
  ranges.push(current);

  const formatted = ranges
    .map((r) => `${r.start.replace(':', '.')}-${r.end.replace(':', '.')}`)
    .join(', ');

  const totalMinutes = sorted.reduce(
    (sum, s) => sum + Math.max(0, toMinutes(s.end) - toMinutes(s.start)),
    0,
  );
  return `${formatted} (${formatDurationMinutes(totalMinutes)})`;
}

export function whatsAppLink(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const normalized = digits.startsWith('0') ? `62${digits.slice(1)}` : digits.startsWith('62') ? digits : `62${digits}`;
  return `https://wa.me/${normalized}`;
}

export function isInteractionLocked(hasUnsavedCustom: boolean): boolean {
  return hasUnsavedCustom;
}

export function closedFieldMessage(fieldName: string): string {
  return `${fieldName} tutup, tidak bisa menerima booking`;
}

export function toMinutes(value: string): number {
  const [hour, minute] = value.slice(0, 5).split(':').map(Number);
  return hour * 60 + minute;
}

export function fromMinutes(total: number): string {
  const hour = Math.floor(total / 60);
  const minute = total % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function getFieldSlotStepMinutes(slots: { start: string; end: string }[] = []): number {
  const counts = new Map<number, number>();
  slots.forEach((slot) => {
    const diff = toMinutes(slot.end) - toMinutes(slot.start);
    if (diff > 0) {
      counts.set(diff, (counts.get(diff) ?? 0) + 1);
    }
  });
  if (counts.size === 0) return 60;

  let bestDuration = 60;
  let bestCount = -1;
  counts.forEach((count, duration) => {
    if (count > bestCount || (count === bestCount && duration < bestDuration)) {
      bestDuration = duration;
      bestCount = count;
    }
  });
  return bestDuration;
}

export function hourLabels(
  gridStart: string,
  gridEnd: string,
  stepMinutes = 60,
  slots?: { start: string; end: string }[],
): string[] {
  const safeStep = stepMinutes > 0 ? stepMinutes : 60;

  if (!slots || slots.length === 0) {
    const rawStart = toMinutes(gridStart);
    const rawEnd = toMinutes(gridEnd);
    const start = safeStep === 60 ? Math.floor(rawStart / 60) * 60 : rawStart;
    const end = Math.max(
      safeStep === 60 ? Math.ceil(rawEnd / 60) * 60 : rawEnd,
      start + safeStep,
    );
    const labels: string[] = [];
    for (let minute = start; minute < end; minute += safeStep) {
      labels.push(fromMinutes(minute));
    }
    return labels;
  }

  let minStart = toMinutes(gridStart);
  let maxEnd = toMinutes(gridEnd);
  slots.forEach((slot) => {
    const s = toMinutes(slot.start);
    const e = toMinutes(slot.end);
    if (s < minStart) minStart = s;
    if (e > maxEnd) maxEnd = e;
  });
  maxEnd = Math.max(maxEnd, minStart + safeStep);

  const minuteSet = new Set<number>();
  for (let minute = minStart; minute < maxEnd; minute += safeStep) {
    minuteSet.add(minute);
  }
  slots.forEach((slot) => {
    minuteSet.add(toMinutes(slot.start));
  });

  return [...minuteSet]
    .sort((a, b) => a - b)
    .map((m) => fromMinutes(m));
}

export function hourSpan(start: string, end: string, stepMinutes = 60): number {
  const safeStep = stepMinutes > 0 ? stepMinutes : 60;
  return Math.max(1, Math.round((toMinutes(end) - toMinutes(start)) / safeStep));
}

export function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function parseDateKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function startOfWeek(date: Date): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekday = next.getDay();
  const delta = weekday === 0 ? -6 : 1 - weekday;
  next.setDate(next.getDate() + delta);
  return next;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function monthGridDates(cursor: Date): Date[] {
  const gridStart = startOfWeek(startOfMonth(cursor));
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

export function formatWeekRange(start: Date): string {
  const end = addDays(start, 6);
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()} - ${end.getDate()} ${MONTH_SHORT[end.getMonth()]} ${end.getFullYear()}`;
  }
  if (start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()} ${MONTH_SHORT[start.getMonth()]} - ${end.getDate()} ${MONTH_SHORT[end.getMonth()]} ${end.getFullYear()}`;
  }
  return `${start.getDate()} ${MONTH_SHORT[start.getMonth()]} ${start.getFullYear()} - ${end.getDate()} ${MONTH_SHORT[end.getMonth()]} ${end.getFullYear()}`;
}

export function formatDayTitle(date: Date): string {
  return `${WEEKDAY_ID[date.getDay()]}, ${date.getDate()} ${MONTH_SHORT[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatMonthTitle(date: Date): string {
  return `${MONTH_ID[date.getMonth()]} ${date.getFullYear()}`;
}

export function weekdayShort(date: Date): string {
  const index = date.getDay();
  return WEEKDAY_SHORT[index === 0 ? 0 : index];
}

export function mondayIndex(date: Date): number {
  return date.getDay() === 0 ? 6 : date.getDay() - 1;
}

export function formatRupiah(amount: number): string {
  return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
}

export function formatTimeRange(start: string, end: string): string {
  return `${start.slice(0, 5)} – ${end.slice(0, 5)}`;
}

export function fieldHeading(name: string, sportName: string): string {
  return sportName ? `${name} · ${sportName}` : name;
}

export function selectionKey(date: string, publicId: string): string {
  return `${date}|${publicId}`;
}

export function nowOffsetClass(minute: number): string {
  const steps = [
    'top-0', 'top-[8%]', 'top-[17%]', 'top-[25%]', 'top-[33%]', 'top-[42%]',
    'top-[50%]', 'top-[58%]', 'top-[67%]', 'top-[75%]', 'top-[83%]', 'top-[92%]',
  ];
  return steps[Math.min(11, Math.floor(minute / 5))];
}

export function spanHeightClass(span: number): string {
  const heights: Record<number, string> = {
    1: 'h-[3.75rem]',
    2: 'h-[7.75rem]',
    3: 'h-[11.75rem]',
    4: 'h-[15.75rem]',
    5: 'h-[19.75rem]',
    6: 'h-[23.75rem]',
  };
  return heights[span] ?? 'h-[3.75rem]';
}

const HOUR_TOP = [
  'top-0', 'top-16', 'top-32', 'top-48', 'top-64', 'top-80', 'top-96',
  'top-[28rem]', 'top-[32rem]', 'top-[36rem]', 'top-[40rem]', 'top-[44rem]',
  'top-[48rem]', 'top-[52rem]', 'top-[56rem]', 'top-[60rem]', 'top-[64rem]',
  'top-[68rem]', 'top-[72rem]', 'top-[76rem]', 'top-[80rem]', 'top-[84rem]',
];

export function hourTopClass(index: number): string {
  return HOUR_TOP[index] ?? 'top-0';
}
